import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import { AppContext } from '../../context/AppContext';
import { stopAllMusic } from '../../components/MusicPlayer';
import SinglesRecipient from './SinglesRecipient';
import SinglesTemplate from './SinglesTemplate';
import SinglesMessage from './SinglesMessage';
import SinglesMusic from './SinglesMusic';
import SinglesQuestion from './SinglesQuestion';
import SinglesSend from './SinglesSend';

const STEPS = ['Recipient', 'Template', 'Message', 'Music', 'Question', 'Preview'];

const CreateSingles = () => {
  const navigate = useNavigate();
  const { token } = useParams();
  const { getInvitationByToken } = useContext(AppContext);
  const editMode = !!token;

  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    recipientName: '',
    includeDatePlan: false,
    eventDate: '',
    eventTime: '',
    eventLocation: '',
    templateId: '',
    templateName: '',
    message: '',
    musicTrack: '',
    musicTitle: '',
    question: 'Would you agree to my invitation?',
  });

  useEffect(() => {
    if (editMode && token) {
      const loadExisting = async () => {
        const inv = await getInvitationByToken(token);
        if (inv) {
          setData({
            recipientName: inv.recipient_name || '',
            includeDatePlan: !!inv.event_date,
            eventDate: inv.event_date || '',
            eventTime: inv.event_time || '',
            eventLocation: inv.event_location || '',
            templateId: inv.template_id || '',
            templateName: inv.occasion || '',
            message: inv.message || '',
            musicTrack: inv.music_track || '',
            musicTitle: inv.music_title || '',
            question: inv.invitation_question || 'Would you agree to my invitation?',
          });
        }
      };
      loadExisting();
    }
  }, [editMode, token, getInvitationByToken]);

  // Stop all music when leaving the creation flow entirely
  useEffect(() => {
    return () => {
      stopAllMusic();
    };
  }, []);

  const handleNext = (stepData) => {
    stopAllMusic();
    setData(prev => ({ ...prev, ...stepData }));
    setStep(prev => prev + 1);
  };

  const handleBack = () => {
    stopAllMusic();
    if (step > 0) setStep(prev => prev - 1);
    else navigate('/home');
  };

  const handleGoToStep = (targetStep) => {
    stopAllMusic();
    setStep(targetStep);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="bg-white px-4 py-4 flex items-center shadow-sm z-10 sticky top-0">
        <button onClick={handleBack} className="p-2 -ml-2 text-gray-600">
          <SafeIcon name="ChevronLeft" className="text-2xl" />
        </button>
        <div className="flex-1 text-center">
          <p className="font-semibold text-gray-800 text-sm">
            {editMode ? '✏️ Editing' : STEPS[step]}
          </p>
          <p className="text-[10px] text-gray-400">Step {step + 1} of {STEPS.length}</p>
        </div>
        <div className="w-10" />
      </div>

      <div className="w-full bg-gray-200 h-1">
        <div
          className="bg-gradient-to-r from-pink-500 to-rose-500 h-1 transition-all duration-500"
          style={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
        />
      </div>

      <div className="flex-1 overflow-y-auto">
        {step === 0 && <SinglesRecipient data={data} onNext={handleNext} />}
        {step === 1 && <SinglesTemplate data={data} onNext={handleNext} />}
        {step === 2 && <SinglesMessage data={data} onNext={handleNext} />}
        {step === 3 && <SinglesMusic data={data} onNext={handleNext} />}
        {step === 4 && <SinglesQuestion data={data} onNext={handleNext} />}
        {step === 5 && (
          <SinglesSend
            data={data}
            onBack={handleBack}
            editMode={editMode}
            editToken={token}
            onGoToStep={handleGoToStep}
          />
        )}
      </div>
    </div>
  );
};

export default CreateSingles;